import { v4 as uuid } from 'uuid';
import { assign } from 'lodash';
import { ChangeDetectionStrategy, Component, type OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormControl } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from "primeng/dynamicdialog";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatDesigns from "@medsurf/flat-designs";
import * as FlatFacades from '@medsurf/flat-facades';
import { ZodSharedValidator } from '@medsurf/flat-validators';
import { ModalControlComponent } from "@medsurf/flat-components";

@Component({
    selector: 'medsurf-modality-detail',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './modality-detail.component.html',
    styleUrls: ['./modality-detail.component.scss'],
    standalone: false
})
export class ModalityDetailComponent implements OnInit {
  @ViewChild('nameInputText', {static: false}) nameInputText: FlatDesigns.TextInputFormControlDesign;
  
  isSubmitted = signal(false);
  #modality: FlatModels.ModalityEntityModels.Modality | null;
  modalityForm = new FormGroup(
    {
      name: new FormControl(''),
      shortcut: new FormControl(''),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.ModalityEntityModels.ModalitySchema
      ),
      updateOn: 'change',
    }
  );
  readonly FlatModels = FlatModels;
  
  constructor(public alertControlFacade: FlatFacades.AlertControlFacade,
              public navigationControlFacade: FlatFacades.NavigationControlFacade,
              public constantAuthorFacade: FlatFacades.ConstantAuthorFacade,
              public dialogService: DialogService,
              public translate: TranslateService) {
    // Update User
    this.constantAuthorFacade.modality$.pipe(takeUntilDestroyed()).subscribe((modality) => {
      this.#modality = structuredClone(modality);
      if (this.#modality) {
        this.modalityForm.reset(this.#modality);
      }
      this.isSubmitted.set(false);
    });
  }

  ngOnInit() {
    // Autofocus
    setTimeout(() => {
      this.nameInputText?.inputElement?.nativeElement?.focus();
    });
  }

  onSubmit() {
    if (this.isSubmitted() || this.modalityForm.invalid) {
      return;
    }
    this.isSubmitted.set(true);

    if (this.#modality) {
      // Update Modality
      const modality = assign(this.#modality, this.modalityForm.value);
      this.constantAuthorFacade.requestUpdateModality(modality).subscribe({
        error: (result) => {
          this.isSubmitted.set(false);
          ZodSharedValidator.handleError(result, this.alertControlFacade, this.modalityForm);
        },
        next: () => {
          this.isSubmitted.set(false);
          this.alertControlFacade.requestAddAlert({
            key: 'main',
            detail: 'notification.saved',
            severity: FlatModels.AlertControlModels.AlertType.enum.success,
            life: FlatModels.AlertControlModels.AlertTime.DEFAULT
          });
        }
      });
    } else {
      // Add Modality
      const modality: FlatModels.ModalityEntityModels.Modality = {
        id: uuid(),
        name: this.modalityForm.value.name || '',
        shortcut: this.modalityForm.value.shortcut || '',
      };
      this.constantAuthorFacade.requestAddModality(modality).subscribe({
        error: (result) => {
          this.isSubmitted.set(false);
          ZodSharedValidator.handleError(result, this.alertControlFacade, this.modalityForm);
        },
        next: () => {
          this.isSubmitted.set(false);
          this.navigationControlFacade.requestNavigate(['modality', modality.id]);
          this.alertControlFacade.requestAddAlert({
            key: 'main',
            detail: 'notification.saved',
            severity: FlatModels.AlertControlModels.AlertType.enum.success,
            life: FlatModels.AlertControlModels.AlertTime.DEFAULT
          });
        }
      });
    }
  }

  deleteModality() {
    if (!this.#modality) return;
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.modality_remove'),
      data: {
        label: 'global.modality_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe(remove => {
      if (this.#modality && remove) {
        const modality = this.#modality;
        this.constantAuthorFacade.requestRemoveModality(modality).subscribe({
          error: (result) => {
            ZodSharedValidator.handleError(result, this.alertControlFacade, this.modalityForm);
          },
          next: () => {
            this.navigationControlFacade.requestNavigate(['modalities']);
            this.alertControlFacade.requestAddAlert({
              key: 'main',
              detail: 'notification.saved',
              severity: FlatModels.AlertControlModels.AlertType.enum.success,
              life: FlatModels.AlertControlModels.AlertTime.DEFAULT
            });
          }
        });
      }
    });
  }
}
